import i18next from 'i18next';
import { sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { formatTime } from '@/datetime/dateTime.utilities';

/**
 * Helpers for formatting a relative "time ago" label for a past timestamp.
 *
 * Extracted from the workbench WorkstepIcons so the Agent Builder history
 * dropdowns can reuse the exact same formatting without duplicating the
 * Intl.RelativeTimeFormat plumbing.
 */

const removePeriod = (str: string) => (str.endsWith('.') ? str.slice(0, -1) : str);

/**
 * Wraps Intl.RelativeTimeFormat.formatToParts to split the localized output
 * into its prefix ("about", "a"), numeric value, and trailing suffix ("ago").
 * The unit-aware splitting lets callers render multi-part labels like
 * "about 3 hours 15 minutes ago" without duplicating prefix/suffix words.
 */
export const formatRelative = (rtf: Intl.RelativeTimeFormat, value: number, unit: Intl.RelativeTimeFormatUnit) => {
  const parts = rtf.formatToParts(-value, unit); // Notice the negative value here (time ago)
  const unitParts = parts.filter((part) => part.type === 'integer' || part.type === 'unit');
  const literalParts = parts.filter((part) => part.type === 'literal');

  let agoPrefix = '';
  let formattedValue = unitParts.map((part) => part.value).join('');
  let agoSuffix = '';

  if (literalParts.length === 2) {
    if (parts[0].type === 'literal') {
      agoPrefix = literalParts[0].value.trim();
      formattedValue += removePeriod(literalParts[1].value.trim());
    } else {
      formattedValue += removePeriod(literalParts[0].value.trim());
      agoSuffix = literalParts[1].value.trim();
    }
  } else if (literalParts.length === 1) {
    if (parts[0].type === 'literal') {
      agoPrefix = literalParts[0].value.trim();
    } else {
      const split = literalParts[0].value.trim().split(' ');
      agoSuffix = split.length > 0 ? split.slice(1).join(' ') : '';
      formattedValue += removePeriod(split[0]);
    }
  }

  return { agoPrefix, formattedValue, agoSuffix };
};

/**
 * Formats a delta (in seconds) as a localized "time ago" label, falling back
 * to an absolute timestamp once the delta exceeds one week.
 */
export const formatTimeAgo = (rtf: Intl.RelativeTimeFormat, timeDelta: number, timestamp: number) => {
  const timeParts = [];
  let agoPrefix = '';
  let agoSuffix = '';

  if (timeDelta < 60) {
    const sec = formatRelative(rtf, timeDelta, 'second');
    timeParts.push(sec.formattedValue);
    agoPrefix = sec.agoPrefix;
    agoSuffix = sec.agoSuffix;
  } else if (timeDelta < 3600) {
    const min = Math.floor(timeDelta / 60);
    const minFormat = formatRelative(rtf, min, 'minute');
    timeParts.push(minFormat.formattedValue);
    agoPrefix = minFormat.agoPrefix;
    agoSuffix = minFormat.agoSuffix;

    const sec = timeDelta - min * 60;
    if (sec > 0) {
      const secFormat = formatRelative(rtf, sec, 'second');
      timeParts.push(secFormat.formattedValue);
    }
  } else if (timeDelta < 86400) {
    // Up to one day
    const hour = Math.floor(timeDelta / 3600);
    const hourFormat = formatRelative(rtf, hour, 'hour');
    timeParts.push(hourFormat.formattedValue);
    agoPrefix = hourFormat.agoPrefix;
    agoSuffix = hourFormat.agoSuffix;

    const min = Math.floor((timeDelta % 3600) / 60);
    if (min > 0) {
      const minFormat = formatRelative(rtf, min, 'minute');
      timeParts.push(minFormat.formattedValue);
    }
  } else if (timeDelta < 7 * 86400) {
    const day = Math.floor(timeDelta / 86400);
    const dayFormat = formatRelative(rtf, day, 'day');
    timeParts.push(dayFormat.formattedValue);
    agoPrefix = dayFormat.agoPrefix;
    agoSuffix = dayFormat.agoSuffix;

    const hour = Math.floor((timeDelta % 86400) / 3600);
    if (hour > 0) {
      const hourFormat = formatRelative(rtf, hour, 'hour');
      timeParts.push(hourFormat.formattedValue);
    }
  } else {
    return formatTime(timestamp, sqWorksheetStore.timezone, 'll LTS');
  }

  return `${agoPrefix ? `${agoPrefix} ` : ''}${timeParts.join(' ')}${agoSuffix ? ` ${agoSuffix}` : ''}`;
};

const COMPACT_UNITS: [unit: Intl.RelativeTimeFormatUnit, seconds: number][] = [
  ['year', 365 * 86400],
  ['month', 30 * 86400],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];

/** Compact localized "time ago" label: "just now", "5m ago", "3h ago", "2d ago". Future times read as "just now". */
export const formatCompactTimeAgo = (date: Date | number) => {
  const elapsedSeconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  const match = COMPACT_UNITS.find(([, seconds]) => elapsedSeconds >= seconds);
  if (!match) {
    return i18next.t('JUST_NOW');
  }

  const [unit, unitSeconds] = match;
  const rtf = new Intl.RelativeTimeFormat(sqWorkbenchStore.userLanguage, { numeric: 'always', style: 'narrow' });
  return rtf.format(-Math.floor(elapsedSeconds / unitSeconds), unit);
};
